import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {NotificationType} from '~core/enum';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {noWhitespaceCharacters} from '~shared/directives/form-validators';
import {AuthenticatedUser, Contact, Customer} from '~core/models';
import {ContactManagementService, DefaultNotificationService, StorageService} from '~core/services';
import {ContactManagementTranslationsService} from '~shared/translations';

/**
 * Handles contact management.
 */
@UntilDestroy()
@Component({
    selector: 'app-contact-management-edit',
    templateUrl: 'contact-management-edit.component.html',
    styleUrls: ['../../../customer-overview.component.scss']
})
export class ContactManagementEditComponent implements OnInit, OnDestroy {
    public updateContactForm: UntypedFormGroup;
    public currentContact: Contact;
    public spinnerName: string = 'contactUpdate';
    public dateToday: Date = new Date();
    public authenticatedUser: AuthenticatedUser;

    @Input() public customer: Customer;

    constructor(
        private notificationService: DefaultNotificationService,
        private contactManagementService: ContactManagementService,
        private formBuilder: UntypedFormBuilder,
        private contactManagementTranslations: ContactManagementTranslationsService,
        private spinnerService: SpinnerService,
        public buttonsTranslations: ButtonsTranslations,
        public flTranslations: FormLabelTranslationService,
        private storageService: StorageService,
        public customerOverviewService: CustomerOverViewService
    ) {
    }

    public ngOnDestroy(): void {
    }

    public ngOnInit(): any {
        this.storageService.castAuthenticatedUser.pipe(untilDestroyed(this))
            .subscribe((data: AuthenticatedUser) => this.authenticatedUser = data);
        this.contactManagementService.castEditContact.pipe(untilDestroyed(this))
            .subscribe((data: Contact) => {
                if (data) {
                    this.currentContact = data;
                    this.fillContactForm();
                }
            });
    }

    /**
     * Create the contact form.
     */
    private fillContactForm(): void {
        this.updateContactForm = this.formBuilder.group({
            id: [this.currentContact.id],
            title: [this.currentContact.title, [
                Validators.required,
                Validators.maxLength(64),
                noWhitespaceCharacters
            ]],
            comment: [this.currentContact.comment, [
                Validators.required,
                Validators.maxLength(500),
                noWhitespaceCharacters
            ]],
            customer: [`api/customers/${this.customer.id}`],
            important: [this.currentContact.important]
        });

        if (this.authenticatedUser.id !== this.currentContact.user.id) {
            this.updateContactForm.disable();
        }
    }

    /**
     * Update the contact that you clicked on.
     */
    public updateContact(): void {
        if (this.updateContactForm.valid) {
            this.spinnerService.show(this.spinnerName);
            this.contactManagementService.update(this.updateContactForm.value).pipe(untilDestroyed(this))
                .subscribe({
                    next: (contact: Contact) => {
                        this.spinnerService.hide(this.spinnerName);
                        this.contactManagementService.updateContactList(contact);
                        this.notificationService.showNotification({
                            type: NotificationType.SUCCESS,
                            message: this.contactManagementTranslations.i18nSuccessUpdateContact
                        });
                        this.customerOverviewService.toggleAllModal(false);
                    },
                    error: (error: Error) => {
                        this.spinnerService.hide(this.spinnerName);
                        this.notificationService.showNotification({
                            type: NotificationType.ALERT,
                            message: this.contactManagementTranslations.i18nErrorUpdateContact
                        });
                    }
                });
        }
    }

}
